/**
 * MatchDetails - Detail panel for selected match
 *
 * Features:
 * - Match info display
 * - Photographer/Editor assignments
 * - IPTC template editor (Photo Mechanic style fields)
 * - FTP server assignment
 * - Editor sign-off for live events
 */

import { useState, useMemo } from 'react';
import clsx from 'clsx';
import Button from '../../../../shared/components/ui/Button';
import { formatDateTime, getMatchStatus, getRelativeTime } from '../../utils/dateUtils';
import {
  getEventColor,
  getCompetitionLabel,
  getEventTypeLabel,
  MATCH_STATUS_COLORS,
} from '../../utils/constants';
import { getMatchXmpUrl } from '../../../../api';
import styles from './MatchDetails.module.css';

/**
 * IPTC fields matching API MatchIptcConfig model
 */
const IPTC_FIELDS = [
  { key: 'headline_template', label: 'Headline', type: 'text' },
  { key: 'caption_template', label: 'Caption/Description', type: 'textarea' },
  { key: 'keywords', label: 'Keywords', type: 'text', hint: 'Comma separated' },
  { key: 'title_template', label: 'Title', type: 'text' },
  { key: 'event_name', label: 'Event Name', type: 'text' },
  { key: 'city', label: 'City', type: 'text' },
  { key: 'state', label: 'State/Province', type: 'text' },
  { key: 'country', label: 'Country', type: 'text' },
  { key: 'country_code', label: 'Country Code', type: 'text' },
  { key: 'location', label: 'Location', type: 'text' },
  { key: 'credit', label: 'Credit', type: 'text' },
  { key: 'copyright_notice', label: 'Copyright Notice', type: 'text' },
  { key: 'category', label: 'Category', type: 'text' },
  { key: 'supplemental_categories', label: 'Suppl. Categories', type: 'text', hint: 'Comma separated' },
];

/**
 * Section header component
 */
function SectionHeader({ title, action }) {
  return (
    <div className={styles.sectionHeader}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      {action}
    </div>
  );
}

/**
 * Assignment list component
 */
function AssignmentList({
  title,
  assignments,
  availableUsers,
  onAssign,
  onUnassign,
  canManage,
  showSignOff = false,
  onSignOff,
}) {
  const [showAdd, setShowAdd] = useState(false);

  const unassignedUsers = useMemo(() => {
    const assignedIds = new Set(assignments.map((a) => a.user_id || a.user));
    return availableUsers.filter((u) => !assignedIds.has(u.id));
  }, [assignments, availableUsers]);

  return (
    <div className={styles.assignmentSection}>
      <SectionHeader
        title={title}
        action={
          canManage && unassignedUsers.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAdd(!showAdd)}
            >
              {showAdd ? 'Cancel' : '+ Add'}
            </Button>
          )
        }
      />

      {assignments.length === 0 ? (
        <p className={styles.emptyNote}>No one assigned yet</p>
      ) : (
        <ul className={styles.assignmentList}>
          {assignments.map((assignment) => (
            <li key={assignment.id} className={styles.assignmentItem}>
              <span className={styles.assignmentName}>
                {assignment.username || assignment.user_name || `User ${assignment.user_id}`}
              </span>
              <div className={styles.assignmentActions}>
                {showSignOff && assignment.signed_off && (
                  <span className={styles.signedOff}>Signed off</span>
                )}
                {showSignOff && !assignment.signed_off && onSignOff && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onSignOff(assignment)}
                  >
                    Sign off
                  </Button>
                )}
                {canManage && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => onUnassign(assignment.user_id || assignment.user)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {showAdd && (
        <div className={styles.addAssignment}>
          {unassignedUsers.map((user) => (
            <Button
              key={user.id}
              variant="ghost"
              size="sm"
              onClick={() => {
                onAssign(user.id);
                setShowAdd(false);
              }}
            >
              + {user.username || user.name}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * IPTC Template Editor
 */
function IPTCEditor({ template, onChange, onSave, onDownload, canEdit }) {
  const [localTemplate, setLocalTemplate] = useState(template || {});
  const [isDirty, setIsDirty] = useState(false);

  const handleChange = (key, value) => {
    setLocalTemplate((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const handleSave = () => {
    onChange(localTemplate);
    onSave?.(localTemplate);
    setIsDirty(false);
  };

  // Array fields that need join/split handling
  const ARRAY_FIELDS = ['keywords', 'supplemental_categories'];

  // Get display value for a field - handle array fields specially
  const getFieldValue = (key) => {
    const value = localTemplate[key];
    if (ARRAY_FIELDS.includes(key) && Array.isArray(value)) {
      return value.join(', ');
    }
    return value || '';
  };

  // Set value for a field - convert array fields back to arrays
  const setFieldValue = (key, value) => {
    if (ARRAY_FIELDS.includes(key)) {
      const items = value
        .split(',')
        .map((k) => k.trim())
        .filter((k) => k);
      handleChange(key, items);
    } else {
      handleChange(key, value);
    }
  };

  return (
    <div className={styles.iptcEditor}>
      <SectionHeader
        title="IPTC Template"
        action={
          <div className={styles.iptcActions}>
            {onDownload && (
              <Button variant="ghost" size="sm" onClick={() => onDownload(localTemplate)}>
                Download
              </Button>
            )}
            {canEdit && isDirty && (
              <Button variant="primary" size="sm" onClick={handleSave}>
                Save Template
              </Button>
            )}
          </div>
        }
      />

      <div className={styles.iptcFields}>
        {IPTC_FIELDS.map((field) => (
          <div key={field.key} className={styles.iptcField}>
            <label className={styles.iptcLabel}>
              {field.label}
              {field.hint && <span className={styles.iptcHint}>{field.hint}</span>}
            </label>
            {field.type === 'textarea' ? (
              <textarea
                className={styles.iptcTextarea}
                value={getFieldValue(field.key)}
                onChange={(e) => setFieldValue(field.key, e.target.value)}
                disabled={!canEdit}
                rows={3}
              />
            ) : (
              <input
                type="text"
                className={styles.iptcInput}
                value={getFieldValue(field.key)}
                onChange={(e) => setFieldValue(field.key, e.target.value)}
                disabled={!canEdit}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * FTP Server Assignment
 */
function FTPServers({ servers, assignedServers, onAssign, onUnassign, canManage }) {
  const unassigned = servers.filter((s) => !assignedServers.includes(s.id));
  const assigned = servers.filter((s) => assignedServers.includes(s.id));

  return (
    <div className={styles.ftpSection}>
      <SectionHeader title="FTP Servers" />

      {assigned.length === 0 ? (
        <p className={styles.emptyNote}>No FTP servers assigned</p>
      ) : (
        <ul className={styles.ftpList}>
          {assigned.map((server) => (
            <li key={server.id} className={styles.ftpItem}>
              <span className={styles.ftpName}>{server.name}</span>
              <span className={styles.ftpHost}>{server.host}</span>
              {canManage && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => onUnassign(server.id)}
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canManage && unassigned.length > 0 && (
        <div className={styles.addFtp}>
          <select
            className={styles.ftpSelect}
            onChange={(e) => {
              if (e.target.value) {
                onAssign(e.target.value);
                e.target.value = '';
              }
            }}
            defaultValue=""
          >
            <option value="">Add FTP server...</option>
            {unassigned.map((server) => (
              <option key={server.id} value={server.id}>
                {server.name} ({server.host})
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

/**
 * Main MatchDetails component
 */
export default function MatchDetails({
  match,
  photographers = [],
  editors = [],
  ftpServers = [],
  onEdit,
  onDelete,
  onAssignPhotographer,
  onUnassignPhotographer,
  onAssignEditor,
  onUnassignEditor,
  onEditorSignOff,
  onSaveIPTC,
  onDownloadIPTC,
  onAssignFTP,
  onUnassignFTP,
  onMarkFullTime,
  onUndoFullTime,
  canManage = false,
  isLive = false,
}) {
  const [activeTab, setActiveTab] = useState('info');
  const status = match ? getMatchStatus(match) : null;
  const eventColor = match ? getEventColor(match) : null;

  if (!match) {
    return (
      <div className={styles.empty}>
        <p>Select a match to view details</p>
      </div>
    );
  }

  const photographerAssignments = match.photographer_assignments || [];
  const editorAssignments = match.editor_assignments || [];
  // API returns ftp_target_assignments with nested ftp_target_id
  const assignedFTPServers = (match.ftp_target_assignments || []).map(a => a.ftp_target_id);

  // Show sign-off option when live with 2+ editors
  const showEditorSignOff = isLive && editorAssignments.length >= 2;

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div
            className={styles.colorIndicator}
            style={{ background: eventColor }}
          />
          <div className={styles.headerText}>
            <h2 className={styles.title}>
              {match.home_team_name && match.away_team_name
                ? `${match.home_team_name} vs ${match.away_team_name}`
                : match.name || 'Untitled Event'}
            </h2>
            {match.name && match.home_team_name && (
              <p className={styles.subtitle}>{match.name}</p>
            )}
          </div>
        </div>
        {canManage && (
          <div className={styles.headerActions}>
            <Button variant="ghost" size="sm" onClick={onEdit}>
              Edit
            </Button>
            <Button variant="danger" size="sm" onClick={onDelete}>
              Delete
            </Button>
          </div>
        )}
      </div>

      {/* Status bar */}
      <div className={styles.statusBar}>
        <span
          className={styles.statusPill}
          style={{
            borderColor: MATCH_STATUS_COLORS[status],
            color: MATCH_STATUS_COLORS[status],
          }}
        >
          {(() => {
            if (status === 'live') return 'LIVE';
            if (status === 'upcoming') return 'Upcoming';
            return 'Finished';
          })()}
        </span>
        {status === 'upcoming' && (
          <span className={styles.relative}>{getRelativeTime(match.start_time)}</span>
        )}
        {match.competition && (
          <span className={styles.competition}>{getCompetitionLabel(match.competition)}</span>
        )}
        <span className={styles.eventType}>{getEventTypeLabel(match.event_type)}</span>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          className={clsx(styles.tab, activeTab === 'info' && styles.tabActive)}
          onClick={() => setActiveTab('info')}
        >
          Info
        </button>
        <button
          className={clsx(styles.tab, activeTab === 'iptc' && styles.tabActive)}
          onClick={() => setActiveTab('iptc')}
        >
          IPTC
        </button>
        <button
          className={clsx(styles.tab, activeTab === 'ftp' && styles.tabActive)}
          onClick={() => setActiveTab('ftp')}
        >
          FTP
        </button>
      </div>

      {/* Tab content */}
      <div className={styles.tabContent}>
        {activeTab === 'info' && (
          <>
            {/* Schedule */}
            <div className={styles.infoSection}>
              <SectionHeader title="Schedule" />
              <div className={styles.infoGrid}>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Start</span>
                  <span className={styles.infoValue}>{formatDateTime(match.start_time)}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>End</span>
                  <span className={styles.infoValue}>{formatDateTime(match.end_time)}</span>
                </div>
                {match.venue && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Venue</span>
                    <span className={styles.infoValue}>{match.venue}</span>
                  </div>
                )}
                {match.kick_off_time && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Kick-off</span>
                    <span className={styles.infoValue}>{formatDateTime(match.kick_off_time)}</span>
                  </div>
                )}
                {match.full_time_at && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Full Time</span>
                    <span className={styles.infoValue}>{formatDateTime(match.full_time_at)}</span>
                  </div>
                )}
              </div>
              {canManage && match.kick_off_time && !match.full_time_at && isLive && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    if (confirm('Mark official playing time as ended? This affects photo captions.')) {
                      onMarkFullTime();
                    }
                  }}
                >
                  Full Time
                </Button>
              )}
              {canManage && match.full_time_at && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (confirm('Undo full time marking?')) {
                      onUndoFullTime();
                    }
                  }}
                >
                  Undo Full Time
                </Button>
              )}
            </div>

            {/* Photographers */}
            <AssignmentList
              title="Photographers"
              assignments={photographerAssignments}
              availableUsers={photographers}
              onAssign={onAssignPhotographer}
              onUnassign={onUnassignPhotographer}
              canManage={canManage}
            />

            {/* Editors */}
            <AssignmentList
              title="Editors"
              assignments={editorAssignments}
              availableUsers={editors}
              onAssign={onAssignEditor}
              onUnassign={onUnassignEditor}
              canManage={canManage}
              showSignOff={showEditorSignOff}
              onSignOff={onEditorSignOff}
            />

            {/* Stats */}
            <div className={styles.infoSection}>
              <SectionHeader title="Stats" />
              <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                  <span className={styles.statValue}>{match.photo_count || 0}</span>
                  <span className={styles.statLabel}>Photos</span>
                </div>
                <div className={styles.statCard}>
                  <span className={styles.statValue}>{photographerAssignments.length}</span>
                  <span className={styles.statLabel}>Photographers</span>
                </div>
                <div className={styles.statCard}>
                  <span className={styles.statValue}>{editorAssignments.length}</span>
                  <span className={styles.statLabel}>Editors</span>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'iptc' && (
          <>
            <IPTCEditor
              template={match.iptc_config || {}}
              onChange={() => {}}
              onSave={onSaveIPTC}
              onDownload={onDownloadIPTC}
              canEdit={canManage}
            />
            {match.iptc_config && (
              <div className={styles.xmpDownload}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => window.open(getMatchXmpUrl(match.id), '_blank')}
                >
                  Download XMP
                </Button>
              </div>
            )}
          </>
        )}

        {activeTab === 'ftp' && (
          <FTPServers
            servers={ftpServers}
            assignedServers={assignedFTPServers}
            onAssign={onAssignFTP}
            onUnassign={onUnassignFTP}
            canManage={canManage}
          />
        )}
      </div>
    </div>
  );
}
